import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import API from "../api";
import style from "../style/twoColForm.module.scss";
import btnStyle from "../style/btn.module.scss";
import CommonTable from "../components/CommonTable";
import { UploadOutlined } from "@ant-design/icons";
import CommonButton from "../components/CommonButton";
import { AxiosResponse } from "axios";
import {
  Button,
  Input,
  Select,
  Space,
  TableProps,
  Upload,
  message,
} from "antd";
import SearchFeature from "../partner/SearchFeture";
import { AllowInputType } from "../utils/ValidationReg";
import { bikeBrands } from "./bikeBrandsData";
interface Data {
  _id: string;
  name: string;
  brandName: string;
  imgUrl: string;
  img?: any;
  transmissionType: string;
  engineType: string;
  kerbWeight: string;
  displacement: string;
  mileage: string;
  tankCapacity: string;
  ridingRange: string;
  chargingTime: string;
  motorPower: string;
  bootStorage?: string;
}

function CreateEditBike({
  mode,
  setMode,
  data,
}: {
  mode: "edit" | "create";
  setMode: Dispatch<SetStateAction<"edit" | "create" | "home">>;
  data: Data;
}) {
  const [formDataD, setFormDataD] = useState(data);
  const [errors, setErrors] = useState<any>({});
  const validateForm = () => {
    const newErrors: any = {};
    if (!formDataD.name) newErrors.name = "Name is required.";
    if (!formDataD.brandName) newErrors.brandName = "Brand name is required.";
    if (!formDataD.img && !formDataD.imgUrl)
      newErrors.img = "Image is required.";
    if (!formDataD.transmissionType)
      newErrors.transmissionType = "Transmission type is required.";
    if (!formDataD.engineType)
      newErrors.engineType = "Engine type is required.";
    if (!formDataD.kerbWeight) newErrors.kerbWeight = "KerbWeight is required.";
    if (!formDataD.ridingRange)
      newErrors.ridingRange = "Riding range is required.";

    // Conditional validation based on engine type
    if (formDataD.engineType === "ev") {
      // For EV: require bootStorage, hide displacement, mileage, tankCapacity
      if (!formDataD.bootStorage)
        newErrors.bootStorage = "Boot Storage is required.";
      if (!formDataD.chargingTime)
        newErrors.chargingTime = "Charging time is required.";
      if (!formDataD.motorPower)
        newErrors.motorPower = "Motor power is required.";
    } else if (formDataD.engineType === "motor") {
      // For Motor: require displacement, mileage, tankCapacity, hide chargingTime, motorPower, bootStorage
      if (!formDataD.displacement)
        newErrors.displacement = "Displacement is required.";
      if (!formDataD.mileage) newErrors.mileage = "Mileage is required.";
      if (!formDataD.tankCapacity)
        newErrors.tankCapacity = "Tank capacity is required.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const onSave = async () => {
    if (!validateForm()) {
      message.error("Please fill all required fields.");
      return;
    }

    if (mode === "create" && !formDataD.img)
      return message.error("Image is required");

    const formData = new FormData();

    formData.append("name", formDataD.name);
    formDataD.img && formData.append("image", formDataD.img);
    formData.append("brandName", formDataD.brandName || "");
    formData.append("transmissionType", formDataD.transmissionType || "");
    formData.append("engineType", formDataD.engineType || "");
    formData.append("kerbWeight", formDataD.kerbWeight || "");
    formData.append("displacement", formDataD.displacement || "");
    formData.append("mileage", formDataD.mileage || "");
    formData.append("ridingRange", formDataD.ridingRange || "");
    formData.append("chargingTime", formDataD.chargingTime || "");
    formData.append("motorPower", formDataD.motorPower || "");
    formData.append("tankCapacity", formDataD.tankCapacity || "");
    formData.append("bootStorage", formDataD.bootStorage || "");

    let createBike;

    if (mode === "create") createBike = await API.admin.CreateBike(formData);
    if (mode === "edit")
      createBike = await API.admin.EditBike(formData, formDataD._id);

    if (
      API.validators.checkSuccessCode(createBike as AxiosResponse<any, any>)
    ) {
      message.success("Details uploaded successfully");
      setMode("home");
    } else {
      message.error("Technical Error Occured");
    }
  };

  return (
    <div className={style["container"]}>
      <h1>Add Bike Details</h1>
      <div className={style["wrap-inp"]}>
        <div className={style["inp-container"]}>
          <label htmlFor="name">
            Bike Name<span>*</span>
          </label>
          <Input
            value={formDataD.name}
            onChange={(e) => {
              setFormDataD({
                ...formDataD,
                name: e.target.value,
              });
            }}
            name="name"
            className={errors.name ? style["error-border"] : ""}
          />
        </div>
        <div className={style["inp-container"]}>
          <label htmlFor="brandName">
            Brand Name<span>*</span>
          </label>
          <Select
            value={formDataD.brandName || undefined}
            onChange={(value) => {
              setFormDataD({
                ...formDataD,
                brandName: value,
              });
            }}
            placeholder="Select Brand"
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={bikeBrands.map((brand) => ({
              label: brand,
              value: brand,
            }))}
            status={errors.brandName ? "error" : undefined}
            style={{ width: "100%" }}
          />
          {errors.brandName && <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>{errors.brandName}</div>}
        </div>
      </div>
      <div className={style["wrap-inp"]}>
        <div className={style["inp-container"]}>
          <label htmlFor="transmissionType">
            Transmission Type<span>*</span>
          </label>
          <Select
            value={formDataD.transmissionType || "gear"}
            onChange={(value) => {
              setFormDataD({ ...formDataD, transmissionType: value });
            }}
            options={[
              { value: "gear", label: "Gear" },
              { value: "gearless", label: "No Gear" },
            ]}
            className={errors.transmissionType ? style["error-border"] : ""}
          />
        </div>
        <div className={style["inp-container"]}>
          <label htmlFor="engineType">
            Engine Type<span>*</span>
          </label>
          <Select
            value={formDataD.engineType || "motor"}
            onChange={(value) => {
              setFormDataD({ ...formDataD, engineType: value });
            }}
            options={[
              { value: "ev", label: "EV" },
              { value: "motor", label: "Motor" },
            ]}
            className={errors.engineType ? style["error-border"] : ""}
          />
        </div>
      </div>
      <div className={style["wrap-inp"]}>
        <div className={style["inp-container"]}>
          <label htmlFor="kerbWeight">
            Kerb weight<span>*</span>
          </label>
          <Input
            value={formDataD.kerbWeight}
            onChange={(e) => {
              setFormDataD({
                ...formDataD,
                kerbWeight: AllowInputType(e.target.value, "numeric"),
              });
            }}
            name="kerbWeight"
            className={errors.kerbWeight ? style["error-border"] : ""}
          />
        </div>
        {formDataD.engineType === "motor" ? (
          <div className={style["inp-container"]}>
            <label htmlFor="displacement">
              Displacement<span>*</span>
            </label>
            <Input
              value={formDataD.displacement}
              onChange={(e) => {
                setFormDataD({
                  ...formDataD,
                  displacement: AllowInputType(e.target.value, "numeric"),
                });
              }}
              name="displacement"
              className={errors.displacement ? style["error-border"] : ""}
            />
          </div>
        ) : (
          <div className={style["inp-container"]}>
            <label htmlFor="chargingTime">
              Charging Time (hrs)<span>*</span>
            </label>
            <Input
              value={formDataD.chargingTime}
              onChange={(e) => {
                // Allow numbers and single decimal point
                const value = e.target.value;
                const decimalRegex = /^\d*\.?\d*$/;
                if (value === "" || decimalRegex.test(value)) {
                  setFormDataD({
                    ...formDataD,
                    chargingTime: value,
                  });
                }
              }}
              name="chargingTime"
              className={errors.chargingTime ? style["error-border"] : ""}
            />
          </div>
        )}
      </div>
      {formDataD.engineType === "motor" ? (
        <div className={style["wrap-inp"]}>
          <div className={style["inp-container"]}>
            <label htmlFor="mileage">
              Mileage<span>*</span>
            </label>
            <Input
              value={formDataD.mileage}
              onChange={(e) => {
                setFormDataD({
                  ...formDataD,
                  mileage: AllowInputType(e.target.value, "numeric"),
                });
              }}
              name="mileage"
              className={errors.mileage ? style["error-border"] : ""}
            />
          </div>
          <div className={style["inp-container"]}>
            <label htmlFor="tankCapacity">
              Fuel Capacity<span>*</span>
            </label>
            <Input
              value={formDataD.tankCapacity}
              onChange={(e) => {
                // Allow numbers and single decimal point
                const value = e.target.value;
                const decimalRegex = /^\d*\.?\d*$/;
                if (value === "" || decimalRegex.test(value)) {
                  setFormDataD({
                    ...formDataD,
                    tankCapacity: value,
                  });
                }
              }}
              name="tankCapacity"
              className={errors.tankCapacity ? style["error-border"] : ""}
            />
          </div>
        </div>
      ) : (
        <div className={style["wrap-inp"]}>
          <div className={style["inp-container"]}>
            <label htmlFor="motorPower">
              Motor Power<span>*</span>
            </label>
            <Input
              value={formDataD.motorPower}
              onChange={(e) => {
                setFormDataD({ ...formDataD, motorPower: e.target.value });
              }}
              name="motorPower"
              className={errors.motorPower ? style["error-border"] : ""}
            />
          </div>
          <div className={style["inp-container"]}>
            <label htmlFor="bootStorage">
              Boot Storage (ltr)<span>*</span>
            </label>
            <Input
              value={formDataD.bootStorage || ""}
              onChange={(e) => {
                setFormDataD({
                  ...formDataD,
                  bootStorage: AllowInputType(e.target.value, "numeric"),
                });
              }}
              name="bootStorage"
              className={errors.bootStorage ? style["error-border"] : ""}
            />
          </div>
        </div>
      )}
      <div className={style["wrap-inp"]}>
        <div className={style["inp-container"]}>
          <label htmlFor="ridingRange">
            Riding Range (Kms)<span>*</span>
          </label>
          <Input
            value={formDataD.ridingRange}
            onChange={(e) => {
              setFormDataD({
                ...formDataD,
                ridingRange: AllowInputType(e.target.value, "numeric"),
              });
            }}
            name="ridingRange"
            className={errors.ridingRange ? style["error-border"] : ""}
          />
        </div>
        <div className={style["inp-container"]}>
          <label htmlFor="upload">
            Upload<span>*</span>
          </label>
          <Upload
            beforeUpload={(file) => {
              const fileUrl = URL.createObjectURL(file);

              setFormDataD({
                ...formDataD,
                img: file,
                imgUrl: fileUrl,
              });

              return false;
            }}
            maxCount={1}
            className={errors.img ? style["error-border"] : ""}
            showUploadList={false}
          >
            {formDataD.imgUrl ? (
              <div style={{ cursor: "pointer" }}>
                <img
                  src={formDataD.imgUrl}
                  alt="bike"
                  className={style["uploaded-img"]}
                />
                <p>
                  {formDataD.img
                    ? formDataD.img.name
                    : formDataD.imgUrl.split("/").pop()}
                </p>
              </div>
            ) : (
              <Button
                icon={
                  <UploadOutlined
                    onPointerEnterCapture={() => {}}
                    onPointerLeaveCapture={() => {}}
                  />
                }
              >
                Bike image
              </Button>
            )}
          </Upload>
        </div>
      </div>
      <div className={btnStyle["wrap-submit-cancel"]}>
        <CommonButton color="green" onClick={() => onSave()}>
          Save
        </CommonButton>
        <CommonButton color="secondary" onClick={() => setMode("home")}>
          Cancel
        </CommonButton>
      </div>
    </div>
  );
}

function Bike() {
  const [mode, setMode] = useState("home" as "edit" | "create" | "home");
  const [data, setData] = useState([] as Data[]);
  const [selectedData, setSelectedData] = useState({} as Data);
  const [originalData, setOriginalData] = useState<any[]>([]);

  const onEdit = (record: Data) => {
    setSelectedData(record);
    setMode("edit");
  };

  const onCreate = () => {
    setSelectedData({
      _id: "",
      name: "",
      imgUrl: "",
      brandName: "",
      img: "",
      transmissionType: "gear",
      engineType: "motor",
      kerbWeight: "",
      displacement: "",
      mileage: "",
      tankCapacity: "",
      ridingRange: "",
      chargingTime: "",
      motorPower: "",
      bootStorage: "",
    });
    setMode("create");
  };

  // const onDelete = async (record: Data) => {
  //   const bike = await API.admin.DeleteBike(record._id);

  //   if (!API.validators.checkSuccessCode(bike))
  //     message.error("Technical error");

  //   message.success("Deleted Successfully");
  //   getBikes();
  // };

  const columns: TableProps<Data>["columns"] = [
    {
      title: "Image",
      dataIndex: "imgUrl",
      key: "imgUrl",
      render: (text: any) => (
        <img style={{ width: "40px", height: "40px" }} src={text} alt="img" />
      ),
    },

    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text: any) => <p>{text}</p>,
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a onClick={() => onEdit(record)}>Edit</a>
          {/* <a onClick={() => onDelete(record)}>Delete</a> */}
        </Space>
      ),
    },
  ];

  const getBikes = async () => {
    const getData = await API.admin.GetBikes();

    if (!API.validators.checkSuccessCode(getData)) {
      return message.error("Technical error");
    }

    setData(getData.data?.data);
    setOriginalData(getData.data?.data);
  };

  useEffect(() => {
    getBikes();
  }, [mode]);

  return (
    <div>
      {mode === "home" && (
        <div>
          <div className={style["warp-search-inp"]}>
            <SearchFeature
              originalData={originalData}
              setData={setData}
              keyData="name"
              placeholder="Search By Bike Name"
            />

            <CommonButton color="primary" onClick={() => onCreate()}>
              Add Bike
            </CommonButton>
          </div>

          <CommonTable columns={columns} data={data} />
        </div>
      )}
      {mode === "create" && (
        <CreateEditBike mode="create" setMode={setMode} data={selectedData} />
      )}
      {mode === "edit" && (
        <CreateEditBike mode="edit" setMode={setMode} data={selectedData} />
      )}
    </div>
  );
}

export default Bike;
